import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QuestionGrading from '../QuestionGrading';
import { StudentDetailData } from '../../services/adminService';
import { isQ26SpecialGrading } from '../../utils/examUtils';

interface QuestionScoringModalProps {
  show: boolean;
  questionNumber: string; // e.g., 'Q28', 'Q78'
  student: StudentDetailData | null;
  onClose: () => void;
  onSave: (questionNumber: string, score: string, note: string, criteria?: any) => Promise<void>;
  saving: boolean;
  selectedExam?: any; // For Q26 detection
}

const ModalOverlay = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 24px;
    max-width: 95%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const StudentInfo = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const StudentInfoText = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 4px;

  strong {
    color: #1e293b;
    font-weight: 600;
  }
`;

const CurrentScoreInfo = styled.div<{ hasScore: boolean }>`
  background: ${props => props.hasScore ? '#dcfce7' : '#fee2e2'};
  border: 2px solid ${props => props.hasScore ? '#86efac' : '#fecaca'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const CurrentScoreTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;

const CurrentScoreContent = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.8;

  strong {
    color: #1e293b;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #f1f5f9;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
  ` : `
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover:not(:disabled) {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Q26 Criteria Styles
const CriteriaSection = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
`;

const CriteriaTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CriteriaMaxScore = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const CriteriaOptions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const CriteriaButton = styled.button<{ selected: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.selected ? '#dbeafe' : 'white'};
  color: ${props => props.selected ? '#1d4ed8' : '#64748b'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    border-color: #3b82f6;
    background: ${props => props.selected ? '#dbeafe' : '#f0f9ff'};
  }
`;

const TotalDisplay = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  text-align: center;
`;

const TotalLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 4px;
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: white;
`;

const QuestionScoringModal: React.FC<QuestionScoringModalProps> = ({
  show,
  questionNumber,
  student,
  onClose,
  onSave,
  saving,
  selectedExam
}) => {
  const [selectedScore, setSelectedScore] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');
  
  // Q26 criteria states
  const [contentScore, setContentScore] = useState<number | null>(null);
  const [sequenceScore, setSequenceScore] = useState<number | null>(null);
  const [grammarScore, setGrammarScore] = useState<number | null>(null);
  const [vocabularyScore, setVocabularyScore] = useState<number | null>(null);
  const [penaltyScore, setPenaltyScore] = useState<number | null>(null);

  // Check if Q26 special grading
  const assignmentForQ26 = selectedExam && student ? {
    sinif: String(student.grade || ''),
    fenn: 'Xarici dil' // Buraxılış modunda Q26 sadece Xarici dil'de
  } : null;
  const isQ26 = isQ26SpecialGrading(assignmentForQ26 as any, questionNumber);

  // Get existing grading data if question is already graded
  const gradingField = `${questionNumber}_c`;
  const existingGradingData = student ? (student as any)[gradingField] : null;
  const hasExistingScore = existingGradingData && typeof existingGradingData === 'object' && existingGradingData.score;
  
  // Calculate Q26 total
  const calculateQ26Total = (): number => {
    const content = contentScore ?? 0;
    const sequence = sequenceScore ?? 0;
    const grammar = grammarScore ?? 0;
    const vocabulary = vocabularyScore ?? 0;
    const penalty = penaltyScore ?? 0;
    const total = content + sequence + grammar + vocabulary - penalty;
    return Math.max(0, Math.min(5, total));
  };

  // Initialize with existing data when modal opens or question changes
  useEffect(() => {
    if (show && student) {
      if (isQ26 && existingGradingData && existingGradingData.criteria) {
        // Q26 with criteria
        setContentScore(existingGradingData.criteria.content ?? null);
        setSequenceScore(existingGradingData.criteria.sequence ?? null);
        setGrammarScore(existingGradingData.criteria.grammar ?? null);
        setVocabularyScore(existingGradingData.criteria.vocabulary ?? null);
        setPenaltyScore(existingGradingData.criteria.penalty ?? null);
        setSelectedScore(existingGradingData.score || null);
        setNote(existingGradingData.note || '');
      } else if (hasExistingScore) {
        // Normal grading
        setSelectedScore(existingGradingData.score);
        setNote(existingGradingData.note || '');
        // Reset Q26 criteria
        setContentScore(null);
        setSequenceScore(null);
        setGrammarScore(null);
        setVocabularyScore(null);
        setPenaltyScore(null);
      } else {
        // No existing score
        setSelectedScore(null);
        setNote('');
        setContentScore(null);
        setSequenceScore(null);
        setGrammarScore(null);
        setVocabularyScore(null);
        setPenaltyScore(null);
      }
    }
  }, [show, questionNumber, student, hasExistingScore, existingGradingData, isQ26]);
  
  // Update total score when Q26 criteria change
  useEffect(() => {
    if (isQ26) {
      const total = calculateQ26Total();
      setSelectedScore(total > 0 ? total.toString() : null);
    }
  }, [contentScore, sequenceScore, grammarScore, vocabularyScore, penaltyScore, isQ26]);

  const handleScoreChange = (qNum: number, score: string | null, noteText?: string) => {
    setSelectedScore(score);
    if (noteText !== undefined) {
      setNote(noteText);
    }
  };

  const handleSave = async () => {
    if (isQ26) {
      // Q26 validation
      if (contentScore === null || sequenceScore === null || grammarScore === null || vocabularyScore === null) {
        alert('Zəhmət olmasa bütün meyarları seçin (Məzmun, Ardıcıllıq, Qrammatika, Söz Ehtiyatı)');
        return;
      }
      const criteria = {
        content: contentScore,
        sequence: sequenceScore,
        grammar: grammarScore,
        vocabulary: vocabularyScore,
        penalty: penaltyScore ?? 0
      };
      const total = calculateQ26Total();
      try {
        await onSave(questionNumber, total.toString(), note, criteria);
        onClose();
      } catch (error) {
        console.error('Error saving score:', error);
        alert('Bal yadda saxlanarkən xəta baş verdi');
      }
    } else {
      // Normal grading validation
      if (!selectedScore) {
        alert('Zəhmət olmasa bal seçin');
        return;
      }
      try {
        await onSave(questionNumber, selectedScore, note);
        onClose();
      } catch (error) {
        console.error('Error saving score:', error);
        alert('Bal yadda saxlanarkən xəta baş verdi');
      }
    }
  };

  const questionNumAsNumber = parseInt(questionNumber.replace('Q', ''), 10);

  return (
    <ModalOverlay show={show} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Bal Ver - {questionNumber}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {student && (
          <>
            <StudentInfo>
              <StudentInfoText>
                <strong>Şagird:</strong> {student.firstName} {student.lastName}
              </StudentInfoText>
              <StudentInfoText>
                <strong>Şagird ID:</strong> {student.student_id}
              </StudentInfoText>
              <StudentInfoText>
                <strong>Sinif:</strong> {student.grade}. Sinif
              </StudentInfoText>
            </StudentInfo>

            {hasExistingScore && (
              <CurrentScoreInfo hasScore={true}>
                <CurrentScoreTitle>Mövcud Bal Məlumatları:</CurrentScoreTitle>
                <CurrentScoreContent>
                  <div><strong>Bal:</strong> {existingGradingData.score}</div>
                  {isQ26 && existingGradingData.criteria && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                      <strong>Meyarlar:</strong>
                      <div style={{ marginTop: '4px', fontSize: '12px' }}>
                        Məzmun: {existingGradingData.criteria.content ?? '-'} | 
                        Ardıcıllıq: {existingGradingData.criteria.sequence ?? '-'} | 
                        Qrammatika: {existingGradingData.criteria.grammar ?? '-'} | 
                        Söz Ehtiyatı: {existingGradingData.criteria.vocabulary ?? '-'}
                        {existingGradingData.criteria.penalty > 0 && ` | Cərimə: -${existingGradingData.criteria.penalty}`}
                      </div>
                    </div>
                  )}
                  {existingGradingData.hoca && (
                    <div><strong>Müəllim:</strong> {existingGradingData.hoca} ({existingGradingData.hocaId})</div>
                  )}
                  {existingGradingData.timestamp && (
                    <div>
                      <strong>Tarix:</strong> {new Date(
                        existingGradingData.timestamp.toDate 
                          ? existingGradingData.timestamp.toDate() 
                          : existingGradingData.timestamp
                      ).toLocaleString('az-AZ')}
                    </div>
                  )}
                  {existingGradingData.note && (
                    <div><strong>Qeyd:</strong> {existingGradingData.note}</div>
                  )}
                </CurrentScoreContent>
              </CurrentScoreInfo>
            )}

            {!hasExistingScore && (
              <CurrentScoreInfo hasScore={false}>
                <CurrentScoreTitle>Bu sual hələ yoxlanılmayıb</CurrentScoreTitle>
                <CurrentScoreContent>
                  Admin olaraq bu sual üçün bal verə bilərsiniz.
                </CurrentScoreContent>
              </CurrentScoreInfo>
            )}

            {isQ26 ? (
              <>
                <CriteriaSection>
                  <CriteriaTitle>
                    Məzmun/Tapşırığın Yerinə Yetirilməsi
                    <CriteriaMaxScore>Max: 2</CriteriaMaxScore>
                  </CriteriaTitle>
                  <CriteriaOptions>
                    {[0, 1, 2].map((score) => (
                      <CriteriaButton
                        key={score}
                        selected={contentScore === score}
                        onClick={() => setContentScore(contentScore === score ? null : score)}
                      >
                        {score}
                      </CriteriaButton>
                    ))}
                  </CriteriaOptions>
                </CriteriaSection>

                <CriteriaSection>
                  <CriteriaTitle>
                    Ardıcıllıq/Məntiqi Rabitə
                    <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                  </CriteriaTitle>
                  <CriteriaOptions>
                    {[0, 0.5, 1].map((score) => (
                      <CriteriaButton
                        key={score}
                        selected={sequenceScore === score}
                        onClick={() => setSequenceScore(sequenceScore === score ? null : score)}
                      >
                        {score}
                      </CriteriaButton>
                    ))}
                  </CriteriaOptions>
                </CriteriaSection>

                <CriteriaSection>
                  <CriteriaTitle>
                    Qrammatika
                    <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                  </CriteriaTitle>
                  <CriteriaOptions>
                    {[0, 0.5, 1].map((score) => (
                      <CriteriaButton
                        key={score}
                        selected={grammarScore === score}
                        onClick={() => setGrammarScore(grammarScore === score ? null : score)}
                      >
                        {score}
                      </CriteriaButton>
                    ))}
                  </CriteriaOptions>
                </CriteriaSection>

                <CriteriaSection>
                  <CriteriaTitle>
                    Söz Ehtiyatı
                    <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                  </CriteriaTitle>
                  <CriteriaOptions>
                    {[0, 0.5, 1].map((score) => (
                      <CriteriaButton
                        key={score}
                        selected={vocabularyScore === score}
                        onClick={() => setVocabularyScore(vocabularyScore === score ? null : score)}
                      >
                        {score}
                      </CriteriaButton>
                    ))}
                  </CriteriaOptions>
                </CriteriaSection>

                <CriteriaSection>
                  <CriteriaTitle>
                    Çıxılan Bal/Cərimə
                    <CriteriaMaxScore>Max: 1</CriteriaMaxScore>
                  </CriteriaTitle>
                  <CriteriaOptions>
                    {[0, 0.5, 1].map((score) => (
                      <CriteriaButton
                        key={score}
                        selected={penaltyScore === score}
                        onClick={() => setPenaltyScore(penaltyScore === score ? null : score)}
                      >
                        {score}
                      </CriteriaButton>
                    ))}
                  </CriteriaOptions>
                </CriteriaSection>

                <TotalDisplay>
                  <TotalLabel>Ümumi Bal</TotalLabel>
                  <TotalValue>{calculateQ26Total().toFixed(1)}</TotalValue>
                </TotalDisplay>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                    Qeyd (İstəyə bağlı):
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Bu sual haqqında qeyd yaza bilərsiniz..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      minHeight: '80px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </>
            ) : (
              <QuestionGrading
                questionNumber={questionNumAsNumber}
                onScoreChange={handleScoreChange}
                initialScore={selectedScore}
                initialNote={note}
                readOnly={false}
              />
            )}

            <ModalActions>
              <ActionButton variant="secondary" onClick={onClose} disabled={saving}>
                Ləğv et
              </ActionButton>
              <ActionButton 
                variant="primary" 
                onClick={handleSave} 
                disabled={isQ26 ? (contentScore === null || sequenceScore === null || grammarScore === null || vocabularyScore === null) : (!selectedScore || saving)}
              >
                {saving ? '⏳ Yadda saxlanır...' : '💾 Balı Yadda Saxla'}
              </ActionButton>
            </ModalActions>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default QuestionScoringModal;
